import { Box } from '@mui/material';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import React, { FC, Key, lazy, useEffect } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import { useIsAuthenticated } from './auth/authentication';
import { GlobalNavigationDrawer } from './components/GlobalNavigationDrawer';
import { NavigationDrawer } from './components/NavigationDrawer/NavigationDrawer';
import { AxlePage } from './components/layout/AxlePage';
import { AxleErrorBoundary, ErrorFallback } from './fetching/errorBoundaries';
import { AxleSuspense, FULL_PAGE_SKELETON_HEIGHT, SimpleSkeleton } from './fetching/suspense';
import { NotFoundPage } from './pages/NotFoundPage';
import { FeatureFlag } from './types/featureFlag';
import { isLocal } from './utils/axleEnvironment';
import { Component, DefiniteComponent } from './utils/reactTypes';
import { useFeatureFlagEnabled, useFeatureFlagsEnabled } from './utils/useFeatureFlagEnabled';

// Lazy load all page components for code splitting
const AllVisitsPage = lazy(() =>
    import('./pages/AllVisitsPage/AllVisitsPage').then((m) => ({ default: m.AllVisitsPage })),
);
const BlocksPage = lazy(() =>
    import('./pages/BlocksPage').then((m) => ({ default: m.BlocksPage })),
);
const BookingFlow = lazy(() =>
    import('./pages/BookingFlow/BookingFlow').then((m) => ({ default: m.BookingFlow })),
);
const CreateServiceAreaPage = lazy(() =>
    import('./pages/CRUDServiceAreaPages').then((m) => ({ default: m.CreateServiceAreaPage })),
);
const EditOrViewServiceAreaPage = lazy(() =>
    import('./pages/CRUDServiceAreaPages').then((m) => ({ default: m.EditOrViewServiceAreaPage })),
);
const ClinicianDetailsPage = lazy(() =>
    import('./pages/ClinicianDetailsPage/ClinicianDetailsPage').then((m) => ({
        default: m.ClinicianDetailsPage,
    })),
);
const DashboardPage = lazy(() =>
    import('./pages/DashboardPage/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const DemandPage = lazy(() =>
    import('./pages/DemandPage/DemandPage').then((m) => ({ default: m.DemandPage })),
);
const ForgotPasswordPage = lazy(() =>
    import('./pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const GoogleMapTestingShimPage = lazy(() =>
    import('./components/mapping/GoogleMap/GoogleMapTestingShimPage').then((m) => ({
        default: m.GoogleMapTestingShimPage,
    })),
);
const InsightsPage = lazy(() =>
    import('./pages/InsightsPage').then((m) => ({ default: m.InsightsPage })),
);
const IntakeCapacityPage = lazy(() =>
    import('./pages/IntakeCapacityPage').then((m) => ({ default: m.IntakeCapacityPage })),
);
const LoginPage = lazy(() => import('./pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const MangleDbPage = lazy(() =>
    import('./pages/MangleDbPage').then((m) => ({ default: m.MangleDbPage })),
);
const OrderDetailsPage = lazy(() =>
    import('./pages/OrderDetailsPage/OrderDetailsPage').then((m) => ({
        default: m.OrderDetailsPage,
    })),
);
const OrdersPage = lazy(() =>
    import('./pages/OrdersPage/OrdersPage').then((m) => ({ default: m.OrdersPage })),
);
const PatientDetailsPage = lazy(() =>
    import('./pages/PatientDetailsPage/PatientDetailsPage').then((m) => ({
        default: m.PatientDetailsPage,
    })),
);
const PatientPortalVisitsPage = lazy(() =>
    import('./pages/PatientPortalPages/PatientPortalVisitsPage').then((m) => ({
        default: m.PatientPortalVisitsPage,
    })),
);
const PatientsPage = lazy(() =>
    import('./pages/PatientsPage/PatientsPage').then((m) => ({ default: m.PatientsPage })),
);
const ReportsPage = lazy(() =>
    import('./pages/ReportsPage').then((m) => ({ default: m.ReportsPage })),
);
const ResetPasswordPage = lazy(() =>
    import('./pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const RouteOptimizerAdminPage = lazy(() =>
    import('./pages/RouteOptimizerAdminPage/RouteOptimizerAdminPage').then((m) => ({
        default: m.RouteOptimizerAdminPage,
    })),
);
const RouteOptimizerPage = lazy(() =>
    import('./pages/RouteOptimizerPage/RouteOptimizerPage').then((m) => ({
        default: m.RouteOptimizerPage,
    })),
);
const SSOCallbackPage = lazy(() =>
    import('./pages/SSOCallbackPage').then((m) => ({ default: m.SSOCallbackPage })),
);
const SSOLoginPage = lazy(() =>
    import('./pages/SSOLoginPage').then((m) => ({ default: m.SSOLoginPage })),
);
const ClinicianSchedulesPage = lazy(() =>
    import('./pages/SchedulePage/ClinicianSchedulesPage').then((m) => ({
        default: m.ClinicianSchedulesPage,
    })),
);
const ScheduleVisitPage = lazy(() =>
    import('./pages/ScheduleVisitPages/ScheduleVisitPage').then((m) => ({
        default: m.ScheduleVisitPage,
    })),
);
const ScratchPage = lazy(() =>
    import('./pages/ScratchPage').then((m) => ({ default: m.ScratchPage })),
);
const ServiceAreasPage = lazy(() =>
    import('./pages/ServiceAreasPage').then((m) => ({ default: m.ServiceAreasPage })),
);
const UserSettingsPage = lazy(() =>
    import('./pages/UserSettingsPage/UserSettingsPage').then((m) => ({
        default: m.UserSettingsPage,
    })),
);
const SettingsPage = lazy(() =>
    import('./pages/SettingsPage/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const SupplyPage = lazy(() =>
    import('./pages/SupplyPage/SupplyPage').then((m) => ({ default: m.SupplyPage })),
);
const TaskDetailsPage = lazy(() =>
    import('./pages/TaskDetailsPage').then((m) => ({ default: m.TaskDetailsPage })),
);
const TerritoriesPage = lazy(() =>
    import('./pages/TerritoriesPage/TerritoriesPage').then((m) => ({ default: m.TerritoriesPage })),
);
const VisitBookingPage = lazy(() =>
    import('./pages/VisitBooking/VisitBookingPage').then((m) => ({ default: m.VisitBookingPage })),
);
const VisitDetailsPage = lazy(() =>
    import('./pages/VisitDetailsPage/VisitDetailsPage').then((m) => ({
        default: m.VisitDetailsPage,
    })),
);
const VisitTravelPathDebugPage = lazy(() =>
    import('./pages/VisitDetailsPage/VisitTravelPathDebugPage').then((m) => ({
        default: m.VisitTravelPathDebugPage,
    })),
);
const VisitOrderDetailsPage = lazy(() =>
    import('./pages/VisitOrderDetailsPage').then((m) => ({ default: m.VisitOrderDetailsPage })),
);
const VisitOrdersPage = lazy(() =>
    import('./pages/VisitOrdersPage').then((m) => ({ default: m.VisitOrdersPage })),
);
const VisitRequestDetailsPage = lazy(() =>
    import('./pages/VisitRequestDetailsPage/VisitRequestDetailsPage').then((m) => ({
        default: m.VisitRequestDetailsPage,
    })),
);
const VisitRequestsListPage = lazy(() =>
    import('./pages/VisitRequestsListPage/VisitRequestsListPage').then((m) => ({
        default: m.VisitRequestsListPage,
    })),
);
const LegacyPatientDetailsPage = lazy(() =>
    import('./pages/legacy/LegacyPatientDetailsPage/LegacyPatientDetailsPage').then((m) => ({
        default: m.LegacyPatientDetailsPage,
    })),
);
const LegacyPatientsPage = lazy(() =>
    import('./pages/legacy/LegacyPatientsPage/LegacyPatientsPage').then((m) => ({
        default: m.LegacyPatientsPage,
    })),
);
const LegacyVisitsPage = lazy(() =>
    import('./pages/legacy/LegacyVisitsPage/LegacyVisitsPage').then((m) => ({
        default: m.LegacyVisitsPage,
    })),
);
const QueryTestingShimPage = lazy(() =>
    import('./pages/shims/QueryTestShimPage').then((m) => ({ default: m.QueryTestingShimPage })),
);
const SentryTestingShimPage = lazy(() =>
    import('./pages/shims/SentryTestingShimPage').then((m) => ({
        default: m.SentryTestingShimPage,
    })),
);

type RouteProps = {
    path: string;
    exact?: boolean;
    condition?: boolean;
    withNavigationDrawer?: boolean;
    component: React.LazyExoticComponent<() => Component> | (() => Component);
};

/* Route that wraps pages inside a page-level Suspense and ErrorBoundary. */
const UnauthenticatedRoute = ({
    path,
    exact = true,
    condition = true,
    withNavigationDrawer = false,
    component: Component,
}: RouteProps): DefiniteComponent => {
    const hasGlobalNavigationV2 = useFeatureFlagEnabled(FeatureFlag.GLOBAL_NAVIGATION_V2, {
        skip: !withNavigationDrawer,
    });

    if (!condition) {
        return <NotFoundPage />;
    }
    // Use the route path as the key to reset error boundary on route changes
    const componentKey: Key = path;
    return (
        <Route exact={exact} path={path}>
            {withNavigationDrawer ? (
                hasGlobalNavigationV2 ? (
                    <GlobalNavigationDrawer />
                ) : (
                    <NavigationDrawer />
                )
            ) : null}
            <Box flexGrow={'1'}>
                {/* This is page-level error boundary. It makes it so that you can still switch
                 *   pages with the NavigationDrawer even if a page encounters an error.
                 *
                 * We need to set the key= property so that the error boundary is reset/re-rendered
                 *  when you go to a new page; otherwise it will stay stuck in error mode.
                 *  See: https://stackoverflow.com/q/59056211/1292652
                 */}
                <AxleErrorBoundary
                    key={componentKey}
                    reload={true}
                    fallback={
                        <AxlePage noNavDrawer={!withNavigationDrawer}>
                            <ErrorFallback />
                        </AxlePage>
                    }
                >
                    {/* This is page-level suspense. It makes it so when you are switching between
                     *   pages, the NavigationDrawer doesn't disappear while the new page is loading.
                     *
                     *  Pages are encouraged to have their own inner Suspense elements!!
                     *   It looks prettier if the title of the page renders immediately but the details
                     *   below them have a spinner, than if the title takes time to load too.
                     */}
                    <AxleSuspense
                        customFallback={
                            <AxlePage noNavDrawer={!withNavigationDrawer}>
                                <SimpleSkeleton height={FULL_PAGE_SKELETON_HEIGHT} />
                            </AxlePage>
                        }
                    >
                        <Component />
                    </AxleSuspense>
                </AxleErrorBoundary>
            </Box>
        </Route>
    );
};

// When user has multiple tabs, logging out of one logs out of all
// We do not want sibling tabs to redirect to current page
// This is to prevent 404s if logging into a different account with different permissions
const isEverLoggedInAtom = atom(false);

const useRedirectRoute = () => {
    const isAuthenticated = useIsAuthenticated();
    const isEverLoggedIn = useAtomValue(isEverLoggedInAtom);
    const location = useLocation();

    if (isEverLoggedIn && !isAuthenticated) {
        return '';
    }

    const redirectEncoded = encodeURIComponent(location.pathname + location.search);
    // %2F is encoded "/" and does not need a redirect
    return redirectEncoded !== '%2F' ? `?redirect=${redirectEncoded}` : '';
};

const RedirectToSignin: FC = () => {
    const redirectRoute = useRedirectRoute();

    return <Redirect to={`/sign-in${redirectRoute}`} />;
};

/* Route that kicks user to the login page if they are not authenticated. */
const AuthenticatedRoute = (props: RouteProps): DefiniteComponent => {
    const isAuthenticated = useIsAuthenticated();

    if (!isAuthenticated) {
        return <RedirectToSignin />;
    }

    return <UnauthenticatedRoute withNavigationDrawer={true} {...props} />;
};

/* Define the app routes, and return the current app page. */
export const RootRoutes = () => {
    const isAuthenticated = useIsAuthenticated();
    const setIsEverLoggedIn = useSetAtom(isEverLoggedInAtom);

    useEffect(() => {
        if (isAuthenticated) {
            setIsEverLoggedIn(true);
        }
    }, [isAuthenticated, setIsEverLoggedIn]);

    const { hasNewVisitsList, hasNewPatientsList, hasNewPatientsDetails } = useFeatureFlagsEnabled(
        {
            hasNewVisitsList: FeatureFlag.VISITS_LIST_PAGE_V2,
            hasNewPatientsList: FeatureFlag.PATIENTS_LIST_PAGE_V2,
            hasNewPatientsDetails: FeatureFlag.PATIENT_DETAILS_PAGE_V2,
        },
        { skip: !isAuthenticated },
    );
    return (
        // <Switch /> ensures we render only one Route, instead of all that match.
        <Switch>
            {/* Dashboard is the root route page, and will internally automatically redirect to the visits page if the dashboard feature flag is not enabled */}
            <AuthenticatedRoute exact path='/' component={DashboardPage} />

            {/* Patient portal pages (unauthenticated). */}
            <UnauthenticatedRoute
                path='/patient-portal/:patientUuid/visits'
                component={PatientPortalVisitsPage}
            />

            {/* Login pages (unauthenticated). */}
            <UnauthenticatedRoute path='/sign-in' component={LoginPage} />
            <UnauthenticatedRoute path='/reset-password' component={ResetPasswordPage} />
            <UnauthenticatedRoute path='/forgot-password' component={ForgotPasswordPage} />
            <UnauthenticatedRoute path='/sso' component={SSOLoginPage} />
            <UnauthenticatedRoute path='/sso-callback' component={SSOCallbackPage} />

            {/* Partner admin pages (authenticated). */}
            {/* Note: The detail page routes need to come before the list routes, otherwise it doesn't work.
                Not sure why, since we are using `exact={true}` to define the routes, but be aware. */}
            {/* For simplicity, let's make the ordering here match the ordering of pages in the navigation drawer. */}
            <AuthenticatedRoute path='/visits/:visitId/schedule/' component={ScheduleVisitPage} />
            <Redirect from='/visits/reschedule/:visitId' to='/visits/:visitId/schedule/' />
            <AuthenticatedRoute path='/visits/create' component={VisitBookingPage} />
            <AuthenticatedRoute path='/visits/:visitId' component={VisitDetailsPage} />
            <AuthenticatedRoute
                path='/visits'
                component={hasNewVisitsList ? AllVisitsPage : LegacyVisitsPage}
            />
            <AuthenticatedRoute
                path='/visit-requests/:visitRequestId'
                component={VisitRequestDetailsPage}
            />
            <AuthenticatedRoute path='/visit-requests' component={VisitRequestsListPage} />
            <AuthenticatedRoute path='/route-optimizer/admin' component={RouteOptimizerAdminPage} />
            <AuthenticatedRoute path='/route-optimizer' component={RouteOptimizerPage} />
            <AuthenticatedRoute path='/booking' component={BookingFlow} />
            <AuthenticatedRoute
                path='/patients/:patientId'
                exact={!hasNewPatientsDetails} // New patient details page defines its own subroutes.
                component={hasNewPatientsDetails ? PatientDetailsPage : LegacyPatientDetailsPage}
            />
            {/* Redirect legacy patient detail subroutes to base page */}
            {!hasNewPatientsDetails && (
                <Route
                    path='/patients/:patientId/:subroute'
                    render={({ match }) => <Redirect to={`/patients/${match.params.patientId}`} />}
                />
            )}
            <AuthenticatedRoute
                path='/patients'
                component={hasNewPatientsList ? PatientsPage : LegacyPatientsPage}
            />

            {/* Direct URLs to legacy versions of pages when feature flags are enabled,
            for ease of debugging and reference. */}
            {hasNewPatientsDetails && (
                <AuthenticatedRoute
                    path='/legacy/patients/:patientId'
                    component={LegacyPatientDetailsPage}
                />
            )}
            {hasNewPatientsList && (
                <AuthenticatedRoute path='/legacy/patients' component={LegacyPatientsPage} />
            )}
            {hasNewVisitsList && (
                <AuthenticatedRoute path='/legacy/visits' component={LegacyVisitsPage} />
            )}
            {/* New Orders Page routes - will check for feature flag inside the page to verify access. */}
            <AuthenticatedRoute path='/orders/:orderId' component={OrderDetailsPage} />
            <AuthenticatedRoute path='/orders' component={OrdersPage} />
            {/* Old Orders Page routes */}
            <AuthenticatedRoute
                path='/visit-orders/:visitOrderId'
                component={VisitOrderDetailsPage}
            />
            <AuthenticatedRoute path='/visit-orders' component={VisitOrdersPage} />
            {/*
            Q: Why do we prefix the task details route with `/clinicians/...`?
            A: So that the sidebar label for "Clinicians" is highlighted when on the task
               details page. The task detail page is only gotten to via a Clinician details
               page, and kind of belongs semantically in the "Clinicians" sidebar group of
               pages.

            Note: Because of the `/clinicians/...` prefix, this route must come before the
            `/clinicians` route, otherwise the route matching gets weird, and we get a blank page.
            */}
            <AuthenticatedRoute path='/clinicians/tasks/:taskId' component={TaskDetailsPage} />
            <AuthenticatedRoute path='/clinicians/:clinicianId' component={ClinicianDetailsPage} />
            <AuthenticatedRoute path='/clinicians' component={SupplyPage} />
            <AuthenticatedRoute
                exact={false}
                path='/schedules'
                component={ClinicianSchedulesPage}
            />
            <AuthenticatedRoute path='/territories' component={TerritoriesPage} />
            <AuthenticatedRoute path='/services' component={DemandPage} />
            <AuthenticatedRoute path='/service-areas/create' component={CreateServiceAreaPage} />
            <AuthenticatedRoute
                path='/service-areas/:serviceAreaId'
                component={EditOrViewServiceAreaPage}
            />
            <AuthenticatedRoute path='/service-areas' component={ServiceAreasPage} />
            <AuthenticatedRoute path='/blocks' component={BlocksPage} />
            <AuthenticatedRoute path='/reports' component={ReportsPage} />
            <AuthenticatedRoute path='/insights' component={InsightsPage} />
            <AuthenticatedRoute exact={false} path='/user-settings' component={UserSettingsPage} />
            <AuthenticatedRoute path='/settings' component={SettingsPage} />
            <AuthenticatedRoute path='/patient-intake' component={IntakeCapacityPage} />
            {/* Some development-only test paths (authenticated). */}
            <AuthenticatedRoute
                path='/.../map'
                component={GoogleMapTestingShimPage}
                condition={isLocal}
            />
            {/* formerly isLocal */}
            <AuthenticatedRoute path='/.../scratch' component={ScratchPage} />
            <AuthenticatedRoute
                path='/.../query'
                component={QueryTestingShimPage}
                condition={isLocal}
            />

            {/* Some development and production test paths (authenticated). */}
            <AuthenticatedRoute path='/.../sentry' component={SentryTestingShimPage} />
            <AuthenticatedRoute
                path='/.../visits/:visitId/travel-path-debug'
                component={VisitTravelPathDebugPage}
            />
            {/* This path is hard-coded into the backend. */}
            <AuthenticatedRoute path='/.../mangledb' component={MangleDbPage} />

            {/* Fallback for no matching route (unauthenticated). */}
            <Route component={NotFoundPage} />
        </Switch>
    );
};
